import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal } from "@/components/shared/Reveal";
import { motion } from "framer-motion";
import { AlertTriangle, Send, UserSearch, Navigation, ClipboardCheck, ArrowRightLeft, PackageCheck, ArrowRight, HeartHandshake } from "lucide-react";

const FLOW = [
  { icon: AlertTriangle, label: "Driver Approaching HOS Limit" },
  { icon: Send, label: "Request Relief" },
  { icon: UserSearch, label: "Find Qualified Driver" },
  { icon: Navigation, label: "Driver Travels to Handoff" },
  { icon: ClipboardCheck, label: "Document Handoff" },
  { icon: ArrowRightLeft, label: "Transfer Custody" },
  { icon: PackageCheck, label: "Continue Delivery" },
];

export const HOSRelief = () => {
  return (
    <section data-testid="hos-relief-section" className="py-24 md:py-32 bg-rr-navy relative overflow-hidden">
      <div className="grain-overlay" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="HOS Relief"
          title="Running Out of Hours Doesn't Automatically Mean the Load Stops."
          description="When authorized ELD information indicates a commercial driver may not have sufficient available Hours of Service to complete an assignment, RelayRigs can help identify that a relay may be required. The driver or dispatcher can then request an eligible relief driver."
          light
          testId="hos-relief"
        />

        <Reveal delay={0.3}>
          <div className="mt-16 flex flex-wrap items-center justify-center gap-y-8">
            {FLOW.map((step, i) => (
              <div key={step.label} className="flex items-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  data-testid={`hos-flow-step-${i}`}
                  className="flex flex-col items-center gap-3 w-32 text-center"
                >
                  <div className="w-14 h-14 rounded-full bg-white/[0.06] border border-rr-orange/40 flex items-center justify-center text-rr-orangeLight">
                    <step.icon size={22} />
                  </div>
                  <span className="text-xs font-semibold text-white/80 leading-tight">{step.label}</span>
                </motion.div>
                {i < FLOW.length - 1 && <ArrowRight className="text-white/20 mx-1 hidden md:block" size={18} />}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.35}>
          <div data-testid="never-abandoned-callout" className="mt-14 rounded-[2rem] border border-rr-orange/30 bg-white/[0.04] p-8 md:p-10 flex flex-col md:flex-row items-start gap-6">
            <div className="w-14 h-14 rounded-full bg-rr-orange/15 border border-rr-orange/40 flex items-center justify-center text-rr-orangeLight shrink-0">
              <HeartHandshake size={26} />
            </div>
            <div>
              <h3 className="font-heading text-2xl font-bold text-white">Never Abandoned.</h3>
              <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-2xl">
                A relief driver's job doesn't end at the handoff without a way home. Every relief assignment's secured compensation must account for the relief driver's return trip — a flight or coach bus back to where they were picked up. Arranging and funding that return trip is the responsibility of the requesting owner operator or carrier, built into the assignment terms before the relief driver ever accepts. RelayRigs does not book or provide the travel itself.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.4}>
          <div className="mt-14 flex flex-col md:flex-row items-center justify-between gap-6 border-t border-white/10 pt-8">
            <p className="text-xs text-white/40 max-w-xl">
              RelayRigs does not advertise itself as an ELD unless it becomes a registered, self-certified ELD.
            </p>
            <a href="#carriers" data-testid="learn-driver-relief-cta" className="inline-flex items-center gap-2 bg-rr-orange text-white font-bold px-7 py-3.5 rounded-full hover:bg-white hover:text-rr-navy transition-all hover:-translate-y-1 shrink-0">
              Learn About Driver Relief <ArrowRight size={16} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
